import type { Article } from './types';

const article: Article = {
  slug: 'usfans-spreadsheet-seller-guide',
  order: 4,
  image:
    'https://readdy.ai/api/search-image?query=A%20modern%20professional%20reviewing%20a%20storefront%20profile%20on%20a%20tablet%20with%20a%20magnifying%20glass%20and%20checklist%20nearby%20warm%20olive%20and%20cream%20tones%20soft%20studio%20lighting%20concept%20of%20verifying%20a%20seller&width=800&height=600&seq=art-seller-04&orientation=landscape',
  en: {
    title: 'USFans Spreadsheet Seller Guide',
    cardDesc: 'What to check before trusting a seller or product link.',
    lead:
      'Researching a seller is one of the most important parts of safe shopping. This guide explains what to verify before you trust a seller, product link or offer.',
    category: 'Sellers',
    readingTime: '7 min read',
    sections: [
      {
        h2: 'Why Seller Research Matters',
        paragraphs: [
          'Most issues come from trusting a link or offer without checking it first. A few minutes of research can help you avoid problems with pricing, stock or shipping.',
        ],
      },
      {
        h2: 'What to Check First',
        paragraphs: [
          'Look at the basic details before anything else. Check what is being sold, the stated price and whether the offer matches what you actually want.',
        ],
      },
      {
        h2: 'Compare Multiple Options',
        paragraphs: [
          'Do not rely on a single source. Compare similar products and sellers to see whether a price or detail looks reasonable in context.',
        ],
      },
      {
        h2: 'Verify on the Main Website',
        paragraphs: [
          'The main website is the most reliable place to confirm current prices, stock and shipping. Use it as your final check before making any decision.',
        ],
      },
    ],
    comparisonTitle: 'Individual Seller vs. Seller List',
    comparisonHeaders: ['Method', 'Single Seller', 'Seller List'],
    comparisonRows: [
      { label: 'Detail', a: 'Deep on one seller', b: 'Broad overview' },
      { label: 'Time', a: 'Longer', b: 'Shorter' },
      { label: 'Best for', a: 'Final verification', b: 'Initial comparison' },
    ],
    faqs: [
      {
        q: 'How do I verify a seller?',
        a: 'Check the product details, compare similar offers and confirm the current information on the main website.',
      },
      {
        q: 'What if a price looks too good?',
        a: 'Treat unusually low prices with caution and verify the details independently before acting.',
      },
      {
        q: 'Should I trust one review?',
        a: 'No single source is enough. Look at multiple signals and confirm facts on the main website.',
      },
    ],
  },
  pl: {
    title: 'Przewodnik po sprzedawcach USFans Spreadsheet',
    cardDesc: 'Co sprawdzić, zanim zaufasz sprzedawcy lub linkowi produktu.',
    lead:
      'Badanie sprzedawcy to jedna z najważniejszych części bezpiecznych zakupów. Ten przewodnik wyjaśnia, co zweryfikować, zanim zaufasz sprzedawcy, linkowi lub ofercie.',
    category: 'Sprzedawcy',
    readingTime: '7 min czytania',
    sections: [
      {
        h2: 'Dlaczego badanie sprzedawcy ma znaczenie',
        paragraphs: [
          'Większość problemów wynika z zaufania linkowi lub ofercie bez wcześniejszego sprawdzenia. Kilka minut researchu może pomóc uniknąć problemów z ceną, stanem magazynowym lub wysyłką.',
        ],
      },
      {
        h2: 'Co sprawdzić najpierw',
        paragraphs: [
          'Najpierw sprawdź podstawowe szczegóły. Zobacz, co jest sprzedawane, jaka jest cena i czy oferta pasuje do tego, czego faktycznie chcesz.',
        ],
      },
      {
        h2: 'Porównaj wiele opcji',
        paragraphs: [
          'Nie polegaj na jednym źródle. Porównaj podobne produkty i sprzedawców, aby ocenić, czy cena lub szczegół wygląda sensownie w kontekście.',
        ],
      },
      {
        h2: 'Zweryfikuj na głównej stronie',
        paragraphs: [
          'Główna strona to najbardziej wiarygodne miejsce do potwierdzenia aktualnych cen, stanu magazynowego i wysyłki. Użyj jej jako ostatecznej kontroli.',
        ],
      },
    ],
    comparisonTitle: 'Pojedynczy sprzedawca a lista sprzedawców',
    comparisonHeaders: ['Metoda', 'Jeden sprzedawca', 'Lista sprzedawców'],
    comparisonRows: [
      { label: 'Szczegółowość', a: 'Głęboko o jednym', b: 'Szeroki przegląd' },
      { label: 'Czas', a: 'Dłuższy', b: 'Krótszy' },
      { label: 'Najlepsze do', a: 'Ostatecznej weryfikacji', b: 'Wstępnego porównania' },
    ],
    faqs: [
      {
        q: 'Jak zweryfikować sprzedawcę?',
        a: 'Sprawdź szczegóły produktu, porównaj podobne oferty i potwierdź aktualne informacje na głównej stronie.',
      },
      {
        q: 'Co jeśli cena wygląda zbyt dobrze?',
        a: 'Traktuj wyjątkowo niskie ceny ostrożnie i zweryfikuj szczegóły niezależnie przed działaniem.',
      },
      {
        q: 'Czy ufać jednej recenzji?',
        a: 'Żadne pojedyncze źródło nie wystarczy. Sprawdź wiele sygnałów i potwierdź fakty na głównej stronie.',
      },
    ],
  },
  es: {
    title: 'Guía de vendedores de USFans Spreadsheet',
    cardDesc: 'Qué revisar antes de confiar en un vendedor o enlace de producto.',
    lead:
      'Investigar a un vendedor es una de las partes más importantes de una compra segura. Esta guía explica qué verificar antes de confiar en un vendedor, enlace u oferta.',
    category: 'Vendedores',
    readingTime: '7 min de lectura',
    sections: [
      {
        h2: 'Por qué importa investigar al vendedor',
        paragraphs: [
          'La mayoría de los problemas vienen de confiar en un enlace u oferta sin revisarlo. Unos minutos de investigación ayudan a evitar problemas de precio, stock o envío.',
        ],
      },
      {
        h2: 'Qué revisar primero',
        paragraphs: [
          'Revisa los detalles básicos antes que nada. Mira qué se vende, el precio indicado y si la oferta coincide con lo que realmente quieres.',
        ],
      },
      {
        h2: 'Compara varias opciones',
        paragraphs: [
          'No dependas de una sola fuente. Compara productos y vendedores similares para ver si un precio o detalle parece razonable en contexto.',
        ],
      },
      {
        h2: 'Verifica en el sitio principal',
        paragraphs: [
          'El sitio principal es el lugar más fiable para confirmar precios, stock y envíos actuales. Úsalo como tu verificación final.',
        ],
      },
    ],
    comparisonTitle: 'Vendedor individual vs. lista de vendedores',
    comparisonHeaders: ['Método', 'Un vendedor', 'Lista de vendedores'],
    comparisonRows: [
      { label: 'Detalle', a: 'Profundo en uno', b: 'Resumen amplio' },
      { label: 'Tiempo', a: 'Mayor', b: 'Menor' },
      { label: 'Ideal para', a: 'Verificación final', b: 'Comparación inicial' },
    ],
    faqs: [
      {
        q: '¿Cómo verifico a un vendedor?',
        a: 'Revisa los detalles del producto, compara ofertas similares y confirma la información actual en el sitio principal.',
      },
      {
        q: '¿Y si un precio parece demasiado bueno?',
        a: 'Trata los precios inusualmente bajos con cautela y verifica los detalles de forma independiente.',
      },
      {
        q: '¿Debo confiar en una sola reseña?',
        a: 'Ninguna fuente única basta. Revisa varias señales y confirma los hechos en el sitio principal.',
      },
    ],
  },
  fr: {
    title: 'Guide des vendeurs USFans Spreadsheet',
    cardDesc: 'Ce qu’il faut vérifier avant de faire confiance à un vendeur ou un lien.',
    lead:
      'Rechercher un vendeur est l’une des étapes les plus importantes d’un achat sûr. Ce guide explique quoi vérifier avant de faire confiance à un vendeur, un lien ou une offre.',
    category: 'Vendeurs',
    readingTime: '7 min de lecture',
    sections: [
      {
        h2: 'Pourquoi la recherche de vendeur compte',
        paragraphs: [
          'La plupart des problèmes viennent de la confiance accordée à un lien ou une offre sans vérification. Quelques minutes de recherche évitent les soucis de prix, stock ou expédition.',
        ],
      },
      {
        h2: 'Que vérifier en premier',
        paragraphs: [
          'Regardez d’abord les détails de base. Voyez ce qui est vendu, le prix indiqué et si l’offre correspond à ce que vous voulez vraiment.',
        ],
      },
      {
        h2: 'Comparez plusieurs options',
        paragraphs: [
          'Ne vous fiez pas à une seule source. Comparez des produits et vendeurs similaires pour juger si un prix ou un détail paraît raisonnable.',
        ],
      },
      {
        h2: 'Vérifiez sur le site principal',
        paragraphs: [
          'Le site principal est l’endroit le plus fiable pour confirmer les prix, stocks et expédition actuels. Utilisez-le comme vérification finale.',
        ],
      },
    ],
    comparisonTitle: 'Vendeur individuel ou liste de vendeurs',
    comparisonHeaders: ['Méthode', 'Un vendeur', 'Liste de vendeurs'],
    comparisonRows: [
      { label: 'Détail', a: 'Profond sur un seul', b: 'Aperçu large' },
      { label: 'Temps', a: 'Plus long', b: 'Plus court' },
      { label: 'Idéal pour', a: 'Vérification finale', b: 'Comparaison initiale' },
    ],
    faqs: [
      {
        q: 'Comment vérifier un vendeur ?',
        a: 'Vérifiez les détails du produit, comparez les offres similaires et confirmez l’information sur le site principal.',
      },
      {
        q: 'Et si un prix paraît trop beau ?',
        a: 'Traitez les prix anormalement bas avec prudence et vérifiez les détails de façon indépendante.',
      },
      {
        q: 'Faut-il se fier à un seul avis ?',
        a: 'Aucune source unique ne suffit. Examinez plusieurs signaux et confirmez les faits sur le site principal.',
      },
    ],
  },
};

export default article;